import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Plus, Minus, X } from "lucide-react";
import { SurveyContext } from "../context/surveyContext";
import { SessionContext } from "../context/sessionContext";
import { useUser } from "../context/UserContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Cookies from "js-cookie";
import SurveyFlowHeader from "../components/SurveyFlowHeader";
import {
  clearPendingGenerateDraft,
  clearSurveySpecs,
  readSurveySpecs,
  patchSurveySpecs,
  markStepReached,
  getMaxStepReached,
  clearMaxStepReached,
} from "../utils/surveyFlowStorage";

const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT;

const SURVEY_DB_NAME = "SurveyDB";
const SURVEY_STORE_NAME = "files";

const saveFileToIndexedDB = (key, file) => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(SURVEY_DB_NAME, 1);

    request.onerror = () => reject(request.error);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(SURVEY_STORE_NAME)) {
        db.createObjectStore(SURVEY_STORE_NAME);
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      const tx = db.transaction([SURVEY_STORE_NAME], "readwrite");
      tx.objectStore(SURVEY_STORE_NAME).put(file, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    };
  });
};

const DefineAudience = ({ initialData = {}, className = "" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAudienceData, surveyData, surveyMeta } = useContext(SurveyContext);
  const { isAuthenticated, getUserId } = useContext(SessionContext);
  const { isAdmin, userData } = useUser();

  const RESTRICTED_PLANS = ["Freemium", "Custom"];
  const MIN_RESTRICTED_AUDIENCE = ENVIRONMENT === "production" ? 1200 : 10;
  const MAX_RESTRICTED_AUDIENCE = 2000;
  const planName = userData?.planDetails?.plan_name;
  const isRestrictedPlan = RESTRICTED_PLANS.includes(planName);

  const savedSpecs = readSurveySpecs();
  const incoming = location.state || {};
  const flow =
    incoming.flow === "generated"
      ? "generated"
      : incoming.flow === "uploaded"
        ? "uploaded"
        : savedSpecs.flow === "generated"
          ? "generated"
          : "uploaded";

  const generatedSurveyId = incoming.surveyId || savedSpecs.surveyId;
  const generatedProjectId = incoming.projectId || savedSpecs.projectId;
  const generatedAudienceGroupId =
    incoming.audienceGroupId || savedSpecs.audienceGroupId;
  const generatedSurveyName = incoming.surveyName || savedSpecs.surveyName;
  const generatedRequestId = incoming.requestId || savedSpecs.requestId;
  const generatedFinalSurvey = savedSpecs.finalSurvey || null;
  const savedAudiences = flow === "generated" ? savedSpecs.audiences : null;
  const [audiences, setAudiences] = useState(
    initialData.audiences ||
      savedAudiences || [{ respondent_group: "", count: 1000 }],
  );
  const [balanceErrorType, setBalanceErrorType] = useState(null);

  const [isProcessing, setIsProcessing] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const isAbandonableUploadFlow = flow === "uploaded" && isAuthenticated;
  const [showLeaveWarning, setShowLeaveWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const [maxStepReached] = useState(() => {
    if (flow !== "generated") return "audience";
    markStepReached("audience");
    return getMaxStepReached() || "audience";
  });

  useEffect(() => {
    if (flow !== "generated") return;
    patchSurveySpecs({ audiences });
  }, [audiences, flow]);

  useEffect(() => {
    if (!isAbandonableUploadFlow) return undefined;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isAbandonableUploadFlow]);

  const handleLogoClick = (e) => {
    if (!isAbandonableUploadFlow) return;
    e.preventDefault();
    setPendingNavigation("/");
    setShowLeaveWarning(true);
  };

  const handleConfirmLeave = () => {
    setShowLeaveWarning(false);
    if (pendingNavigation) navigate(pendingNavigation);
  };

  const handleDescriptionChange = (index, value) => {
    setAudiences((prev) =>
      prev.map((audience, i) =>
        i === index ? { ...audience, respondent_group: value } : audience,
      ),
    );
  };

  const handleSamplesChange = (index, value) => {
    const numValue = Math.max(1, Math.min(10000, parseInt(value) || 250));
    setAudiences((prev) =>
      prev.map((audience, i) =>
        i === index ? { ...audience, count: numValue } : audience,
      ),
    );
  };

  const incrementSamples = (index) => {
    setAudiences((prev) =>
      prev.map((audience, i) =>
        i === index
          ? { ...audience, count: Math.min(10000, audience.count + 250) }
          : audience,
      ),
    );
  };

  const decrementSamples = (index) => {
    setAudiences((prev) =>
      prev.map((audience, i) =>
        i === index
          ? { ...audience, count: Math.max(250, audience.count - 250) }
          : audience,
      ),
    );
  };

  const addNewAudience = () => {
    setAudiences((prev) => [...prev, { respondent_group: "", count: 1000 }]);
  };

  const removeAudience = (index) => {
    if (audiences.length > 1) {
      setAudiences((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const isFormValid = () => {
    return audiences.every(
      (audience) => audience.respondent_group.trim() && audience.count > 0,
    );
  };

  const getSurveyName = () => {
    if (flow === "generated" && generatedSurveyName) {
      return generatedSurveyName;
    }
    if (surveyData && surveyData.name) {
      return surveyData.name.replace(/\.[^/.]+$/, "");
    }
    return "Untitled Survey";
  };

  const getTotalAudienceSize = () => {
    return audiences.reduce((total, audience) => total + audience.count, 0);
  };

  const handleContinue = async () => {
    if (!isFormValid()) return;

    if (isRestrictedPlan) {
      const totalAudienceSize = getTotalAudienceSize();
      if (
        totalAudienceSize < MIN_RESTRICTED_AUDIENCE ||
        totalAudienceSize > MAX_RESTRICTED_AUDIENCE
      ) {
        toast.error(
          `Your total audience is ${totalAudienceSize.toLocaleString()}. For your plan, total audience size needs to be between ${MIN_RESTRICTED_AUDIENCE.toLocaleString()} and ${MAX_RESTRICTED_AUDIENCE.toLocaleString()}.`,
        );
        return;
      }
    }

    setAudienceData(audiences);

    if (isAuthenticated) {
      setIsProcessing(true);

      try {
        const token = Cookies.get("token");

        if (!token) {
          navigate("/log-in");
          return;
        }

        let surveyId = flow === "generated" ? generatedSurveyId : null;
        let projectId = flow === "generated" ? generatedProjectId : null;
        let audienceGroupId =
          flow === "generated" ? generatedAudienceGroupId : null;
        let hasError = false;
        const totalAudienceSize = getTotalAudienceSize();

        if (flow === "generated" && !surveyId) {
          if (!generatedFinalSurvey || !generatedRequestId) {
            console.error("Missing generated survey data to save");
            hasError = true;
            setIsProcessing(false);
            return;
          }

          const questionCount = (generatedFinalSurvey.sections || []).reduce(
            (sum, sec) =>
              sum + (Array.isArray(sec.questions) ? sec.questions.length : 0),
            0,
          );

          const saveRes = await fetch(
            `${process.env.REACT_APP_API_URL}/project/saveGeneratedSurvey`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json", token: token },
              body: JSON.stringify({
                survey: generatedFinalSurvey,
                requestId: generatedRequestId,
                questionCount,
              }),
            },
          );

          const saveData = await saveRes.json();

          if (!saveRes.ok || !saveData.success) {
            console.error("Saving generated survey failed");
            hasError = true;
            setIsProcessing(false);
            return;
          }

          surveyId = saveData.surveyId;
          projectId = saveData.projectId;
          audienceGroupId = saveData.audianceGroupId;
        }

        if (flow === "uploaded" && surveyData) {
          const formDataObj = new FormData();
          formDataObj.append("file", surveyData);
          formDataObj.append("totalAudianceSize", totalAudienceSize);
          if (surveyMeta) {
            formDataObj.append("questionCount", surveyMeta.questionCount);
            formDataObj.append(
              "questionCountConfidence",
              surveyMeta.questionCountConfidence,
            );
            formDataObj.append("surveyTheme", surveyMeta.surveyTheme);
          }

          const surveyRes = await fetch(
            `${process.env.REACT_APP_API_URL}/project/uploadSurvey`,
            {
              method: "POST",
              headers: {
                token: token,
              },
              body: formDataObj,
            },
          );

          const surveyResData = await surveyRes.json();
          // SUCCESS PATH

          if (surveyRes.ok && surveyResData.surveyId) {
            surveyId = surveyResData.surveyId;
            projectId = surveyResData.projectId;
            audienceGroupId = surveyResData.audianceGroupId;
          }

          // ERROR PATH
          else {
            console.error("Survey upload failed");
            const msg = (
              surveyResData?.message ||
              surveyResData?.error ||
              ""
            ).toLowerCase();

            if (msg.includes("insufficiant")) {
              if (isAdmin) {
                setBalanceErrorType("INSUFFICIENT");
                setShowBalanceModal(true);
                setIsProcessing(false);
                return;
              }
              setBalanceErrorType("INSUFFICIENT");
              setShowBalanceModal(true);
              setIsProcessing(false);
              setTimeout(() => {
                navigate("/projects");
              }, 3000);
              return;
            }

            if (msg.includes("project limit")) {
              if (isAdmin) {
                setBalanceErrorType("PROJECT_LIMIT");
                setShowBalanceModal(true);
                setIsProcessing(false);
                return;
              }
              setBalanceErrorType("PROJECT_LIMIT");
              setShowBalanceModal(true);
              setIsProcessing(false);
              setTimeout(() => {
                navigate("/projects");
              }, 3000);
              return;
            }

            hasError = true;
            setIsProcessing(false);
            return;
          }
        }

        // Upload Audience
        if (surveyId && audiences.length > 0) {
          const audienceRes = await fetch(
            `${process.env.REACT_APP_API_URL}/project/createAndUpdateAudience`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                token: token,
              },
              body: JSON.stringify({
                surveyId,
                audienceList: audiences,
              }),
            },
          );

          const audienceResData = await audienceRes.json();

          if (!audienceRes.ok) {
            console.error("Audience upload failed");
            hasError = true;
            setIsProcessing(false);
            return;
          } else {
          }
        }

        // Create Project
        if (projectId && surveyId && audienceGroupId && audiences.length > 0) {
          const surveyName = getSurveyName();

          const projectRes = await fetch(
            `${process.env.REACT_APP_API_URL}/project/createAndUpdateProject`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                token: token,
              },
              body: JSON.stringify({
                projectId: projectId,
                surveyId: surveyId,
                audianceGroupId: audienceGroupId,
                audienceDefinitions: audiences,
                surveyName: surveyName,
                flow: flow,
              }),
            },
          );

          const projectResData = await projectRes.json();

          if (!projectRes.ok) {
            console.error("Project creation failed");

            const msg = (
              projectResData?.message ||
              projectResData?.error ||
              ""
            ).toLowerCase();

            if (msg.includes("insufficiant")) {
              setBalanceErrorType("INSUFFICIENT");
              setShowBalanceModal(true);
              setIsProcessing(false);
              if (!isAdmin) {
                setTimeout(() => navigate("/projects"), 3000);
              }
              return;
            }

            if (msg.includes("project limit")) {
              setBalanceErrorType("PROJECT_LIMIT");
              setShowBalanceModal(true);
              setIsProcessing(false);
              if (!isAdmin) {
                setTimeout(() => navigate("/projects"), 3000);
              }
              return;
            }

            hasError = true;
            setIsProcessing(false);
            return;
          } else {
          }
        }

        if (!hasError) {
          if (flow === "generated") {
            clearPendingGenerateDraft(getUserId?.());
            clearSurveySpecs();
            clearMaxStepReached();
          }

          // Pass project data to GeneratingInsights
          navigate("/generate-insights", {
            state: {
              projectId: projectId,
              surveyId: surveyId,
              audienceGroupId: audienceGroupId,
              surveyName: getSurveyName(),
              createdAt: new Date().toISOString(),
            },
          });
        }
      } catch (error) {
        console.error("Error saving project:", error);
      } finally {
        setIsProcessing(false);
      }
    } else {
      try {
        if (flow === "generated") {
          sessionStorage.setItem(
            "pendingProjectMetadata",
            JSON.stringify({
              flow: "generated",
              requestId: generatedRequestId,
              finalSurvey: generatedFinalSurvey,
              surveyName: getSurveyName(),
              audienceData: audiences,
            }),
          );
        } else if (surveyData) {
          await saveFileToIndexedDB("surveyFile", surveyData);
          sessionStorage.setItem(
            "pendingProjectMetadata",
            JSON.stringify({
              flow: "uploaded",
              surveyMeta: surveyMeta || null,
              audienceData: audiences,
            }),
          );
        }
      } catch (err) {
        console.error("Failed to save progress before sign-up:", err);
      }

      navigate("/pricing");
    }
  };

  return (
    <div className={`page-bg`}>
      <SurveyFlowHeader
        flow={flow === "generated" ? "generated" : "uploaded"}
        activeStep="audience"
        isAuthenticated={isAuthenticated}
        maxStepReached={maxStepReached}
        onLogoClick={handleLogoClick}
        rightSlot={
          isAuthenticated && (
            <Link to="/projects">
              <button className="navbar-btn-secondary">
                Back to Projects →
              </button>
            </Link>
          )
        }
      />
      <div className="content-center">
        <div className="define-audience__container">
          <div className="define-audience__content">
            <h1 className="define-audience__title">Define Your Audience</h1>
            <div className="define-audience__audiences">
              {audiences.map((audience, index) => (
                <div key={index} className="define-audience__audience-item">
                  <div className="define-audience__audience-header">
                    <h3 className="define-audience__audience-title">
                      Audience {index + 1}
                    </h3>
                    {audiences.length > 1 && (
                      <button
                        type="button"
                        className="define-audience__remove-button"
                        onClick={() => removeAudience(index)}
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>
                  <div className="define-audience__field">
                    <label className="define-audience__label">
                      Describe your audience
                    </label>
                    <textarea
                      className="define-audience__textarea"
                      placeholder="e.g., Working professionals aged 25-40 who use mobile apps daily"
                      value={audience.respondent_group}
                      onChange={(e) =>
                        handleDescriptionChange(index, e.target.value)
                      }
                      rows={3}
                    />
                  </div>
                  <br />
                  <div className="define-audience__field">
                    <label className="define-audience__label">
                      Targeted Sample Size
                    </label>
                    <div className="define-audience__number-input">
                      <button
                        type="button"
                        className="define-audience__number-button"
                        onClick={() => decrementSamples(index)}
                        disabled={audience.count <= 1}
                      >
                        <Minus size={16} />
                      </button>
                      <input
                        type="number"
                        className="define-audience__number-field"
                        value={audience.count}
                        onChange={(e) =>
                          handleSamplesChange(index, e.target.value)
                        }
                        min="1"
                        max="10000"
                      />
                      <button
                        type="button"
                        className="define-audience__number-button"
                        onClick={() => incrementSamples(index)}
                        disabled={audience.count >= 10000}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {isRestrictedPlan && (
              <p
                className="define-audience__label"
                style={{
                  marginTop: "-8px",
                  marginBottom: "12px",
                  color:
                    getTotalAudienceSize() < MIN_RESTRICTED_AUDIENCE ||
                    getTotalAudienceSize() > MAX_RESTRICTED_AUDIENCE
                      ? "#f87171"
                      : "inherit",
                }}
              >
                Total audience: {getTotalAudienceSize().toLocaleString()} (needs
                to be between {MIN_RESTRICTED_AUDIENCE.toLocaleString()} and{" "}
                {MAX_RESTRICTED_AUDIENCE.toLocaleString()} for your plan)
              </p>
            )}
            <div className="define-audience__add-section">
              <button
                type="button"
                className="define-audience__add-button"
                onClick={addNewAudience}
              >
                <Plus size={20} />
                Add Another Audience
              </button>
            </div>
            <div className="define-audience__actions">
              <button
                className={`define-audience__continue-button ${
                  !isFormValid() || isProcessing
                    ? "define-audience__continue-button--disabled"
                    : ""
                }`}
                onClick={handleContinue}
                disabled={!isFormValid() || isProcessing}
              >
                {isProcessing ? "Processing..." : "Continue"}
              </button>
            </div>
          </div>
        </div>
        <ToastContainer position="top-right" autoClose={3000} />
        <Dialog
          open={showBalanceModal}
          onClose={() => setShowBalanceModal(false)}
          sx={{
            "& .MuiBackdrop-root": {
              backgroundColor: "rgba(0, 0, 0, 0.8)",
            },
          }}
          PaperProps={{
            sx: {
              backgroundColor: "#000000",
              border: "2px solid #3b82f6",
              borderRadius: "8px",
              color: "white",
              width: "500px",
              maxWidth: "90vw",
              minHeight: "auto",
              position: "relative",
            },
          }}
        >
          <DialogTitle
            sx={{
              backgroundColor: "#000000",
              color: "white",
              textAlign: "center",
              fontSize: "1.3rem",
              fontWeight: "bold",
              paddingBottom: "20px",
              marginBottom: "10px",
              position: "relative",
            }}
          >
            Insufficient Balance
            {isAdmin && (
              <IconButton
                aria-label="close"
                onClick={() => setShowBalanceModal(false)}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: "#3b82f6",
                  "&:hover": {
                    backgroundColor: "rgba(59, 130, 246, 0.1)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </DialogTitle>
          <DialogContent
            sx={{
              backgroundColor: "#000000",
              color: "white",
              textAlign: "center",
              padding: "15px 20px",
              fontSize: "1rem",
            }}
          >
            {balanceErrorType === "INSUFFICIENT" && (
              <>
                <div>Your account has insufficient balance.</div>
                <div style={{ marginTop: "6px" }}>
                  Please add credits to continue.
                </div>
              </>
            )}
            {balanceErrorType === "PROJECT_LIMIT" && (
              <>
                <div>You have reached project limits.</div>
                <div style={{ marginTop: "6px" }}>
                  Please upgrade your plan to continue.
                </div>
              </>
            )}
          </DialogContent>
          {isAdmin && (
            <DialogActions
              sx={{
                backgroundColor: "#000000",
                justifyContent: "center",
                paddingBottom: "15px",
                gap: "12px",
              }}
            >
              <Button
                variant="contained"
                onClick={() => {
                  if (balanceErrorType === "INSUFFICIENT") {
                    navigate("/subscription");
                  } else if (balanceErrorType === "PROJECT_LIMIT") {
                    navigate("/subscription");
                  }
                }}
                sx={{
                  backgroundColor: "#3b82f6",
                  color: "white",
                  padding: "10px 26px",
                  "&:hover": {
                    backgroundColor: "#2563eb",
                  },
                }}
              >
                {balanceErrorType === "INSUFFICIENT"
                  ? "Add Credits"
                  : "Upgrade Plan"}
              </Button>
              <Button
                onClick={() => navigate("/projects")}
                sx={{
                  color: "#ffffffff",
                  border: "1px solid #ffffffff",
                  padding: "10px 26px",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    borderColor: "#666",
                  },
                }}
              >
                Back to Projects
              </Button>
            </DialogActions>
          )}
        </Dialog>
        <Dialog
          open={showLeaveWarning}
          onClose={() => setShowLeaveWarning(false)}
          sx={{
            "& .MuiBackdrop-root": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
          }}
          PaperProps={{
            sx: {
              backgroundColor: "#000000",
              border: "2px solid #3b82f6",
              borderRadius: "8px",
              color: "white",
              width: "500px",
              maxWidth: "90vw",
            },
          }}
        >
          <DialogTitle
            sx={{
              backgroundColor: "#000000",
              color: "white",
              textAlign: "center",
              fontSize: "1.3rem",
              fontWeight: "bold",
              position: "relative",
            }}
          >
            Leave without finishing?
            <IconButton
              aria-label="close"
              onClick={() => setShowLeaveWarning(false)}
              sx={{ position: "absolute", right: 8, top: 8, color: "#3b82f6" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              backgroundColor: "#000000",
              color: "white",
              textAlign: "center",
              padding: "15px 20px",
            }}
          >
            <div>
              Uploaded surveys aren't saved until this step is complete.
            </div>
            <div style={{ marginTop: "12px" }}>
              If you leave now, all progress will be lost and you'll need to
              start over.
            </div>
          </DialogContent>
          <DialogActions
            sx={{
              backgroundColor: "#000000",
              justifyContent: "center",
              paddingBottom: "15px",
              gap: "12px",
            }}
          >
            <Button
              onClick={() => setShowLeaveWarning(false)}
              sx={{
                color: "#ffffff",
                border: "1px solid #ffffff",
                padding: "10px 26px",
              }}
            >
              Stay and Finish
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmLeave}
              sx={{
                backgroundColor: "#3b82f6",
                color: "white",
                padding: "10px 26px",
              }}
            >
              Leave Anyway
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default DefineAudience;
